import * as React from "react";
import OfficeThemeSync from "./useOfficeThemeSync";
import { ColorPreference, PaneMode, parseColorPreference } from "../platform/shared/officeTheme";

// The user's light / dark / system choice from the header menu.
//
// Stored in localStorage, which is per machine and per add-in origin. The alternatives
// were checked and are worse: Office's roaming settings exist in Outlook only, and the
// document's own settings would write the choice INTO THE FILE, so it would travel with
// the document to whoever opens it next. A per-machine preference is the honest scope
// for "how I like my pane to look".
//
// Only an explicit "light" or "dark" is written; choosing "system" removes the key. So
// the thing Providers.tsx used to guard against -- a remembered scheme from last week
// silently overriding what Office says today -- can only happen if the user asked for
// exactly that, and the menu always shows that they did.

const STORAGE_KEY = "jylo.colorPreference";

/** Never throws: storage can be missing, blocked, or full, and none of that is worth a crash. */
const readStoredPreference = (): ColorPreference => {
  try {
    return parseColorPreference(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return "system";
  }
};

const storePreference = (preference: ColorPreference) => {
  try {
    if (preference === "system") window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // The choice still applies for this session; it just will not be remembered.
  }
};

/**
 * Puts the scheme on the page: `.dark` on <html> is what palette.css and Tailwind's
 * `dark:` variant key off. One class on the root rather than anything per component,
 * so switching is a single attribute change and nothing re-renders to pick it up.
 */
export const applyPaneMode = (mode: PaneMode) => {
  const root = document.documentElement;
  root.classList.toggle("dark", mode === "dark");
  root.classList.toggle("light", mode === "light");
};

interface ColorPreferenceContextValue {
  preference: ColorPreference;
  setPreference: (next: ColorPreference) => void;
  /** The scheme on screen: the preference, or Office's theme when the preference is "system". */
  mode: PaneMode;
}

const ColorPreferenceContext = React.createContext<ColorPreferenceContextValue | null>(null);

/** Holds the preference, resolves it against Office's theme, and applies the result. */
export const ColorPreferenceProvider = ({ children }: { children: React.ReactNode }) => {
  const [preference, setPreferenceState] = React.useState<ColorPreference>(readStoredPreference);
  const [mode, setMode] = React.useState<PaneMode>("light");

  // Layout effect so the class is on <html> before the first paint, not one frame after.
  React.useLayoutEffect(() => applyPaneMode(mode), [mode]);

  const setPreference = React.useCallback((next: ColorPreference) => {
    storePreference(next);
    setPreferenceState(next);
  }, []);

  const value = React.useMemo(
    () => ({ preference, setPreference, mode }),
    [preference, setPreference, mode]
  );

  return (
    <ColorPreferenceContext.Provider value={value}>
      <OfficeThemeSync preference={preference} onMode={setMode} />
      {children}
    </ColorPreferenceContext.Provider>
  );
};

export const useColorPreference = (): ColorPreferenceContextValue => {
  const context = React.useContext(ColorPreferenceContext);
  if (!context) throw new Error("useColorPreference must be used inside ColorPreferenceProvider.");
  return context;
};
